/**
 * voice-v8 — die Messzeile `[ende-token]`: kommt Sonioxʼ Satzende-Signal
 * `<end>` früher als Jev, und trennt es Martins Satzenden?
 * (01.10.2026, Ticket 2867 — NUR MESSEN, keine Wirkung auf die Entscheidung)
 *
 * ## Woher das kommt
 *
 * Soniox liefert mit `enable_endpoint_detection: true` ein semantisches
 * Satzende-Signal (Pausen, Intonation, Sprechmuster, Gesprächskontext —
 * https://soniox.com/docs/stt/rt/endpoint-detection): ein eigenes Token
 * `{"text": "<end>", "is_final": true}` im Strom. Es kam in unserer
 * Mitschrift schon immer an und wurde verworfen (live-dictation.ts, die
 * Steuer-Tokens). Jev sieht NUR Text (shared/stille-auto.ts) — `<end>` hört
 * die Stimme. Ob es Martins Satzenden von seinen Denkpausen trennt und ob es
 * früher kommt als Jevs Urteil, weiß niemand; hier wird es gemessen.
 *
 * ## Die Zeile — je Aufnahme genau eine, nur Zahlen, nie ein Wort Text
 *
 *   [ende-token] live=ja vorhanden=ja anzahl=2 davonWeiter=1 anzahlAbFrist=1
 *     davonWeiterAbFrist=1 weiterMaxStillMs=2600 pausenWeiter=1 abstandMs=850
 *     stillMsBeiToken=2600 weitergesprochen=nein jev=0.74 regel=stufen
 *     entscheid=urteil stufe=2 stillMs=2600 textRuhtMs=2700
 *
 *   live             Lieferte die Live-Mitschrift in dieser Aufnahme Text? Nein
 *                    heißt: nativer Weg oder Dateierkennung — dort gibt es kein
 *                    `<end>`, „vorhanden=nein“ ist dann keine Aussage über die
 *                    Erkennung. (Nur der Live-Weg setzt `enable_endpoint_
 *                    detection`; die Dateierkennung am Server kennt den
 *                    Parameter nicht.)
 *   vorhanden        Kam mindestens ein `<end>` an?
 *   anzahl           Wie viele (ein Satz je Token, wenn es taugt).
 *   davonWeiter      Auf wie viele folgten noch bestätigte Wörter. Jedes davon
 *                    ist ein Satzende-Signal, nach dem es weiterging.
 *
 *   ⚠️ Atempause oder Denkpause? Ein `<end>` nach 600 ms Stille und eines nach
 *   2600 ms sind verschiedene Dinge: das erste liegt unter jeder Frist von
 *   Jev (AUTO_ERSTE_FRIST_MS, 1,5 s), das zweite ist die Lage, in der Jev
 *   fragt und die Stufen greifen. Ohne diese Trennung ließ sich die Frage
 *   „trennt `<end>` Satzenden von Denkpausen?“ nicht beantworten (Nachprüfung
 *   01.10.2026: beide Fälle schrieben dieselbe Zeile).
 *   anzahlAbFrist        Tokens, die bei mindestens AUTO_ERSTE_FRIST_MS Stille ankamen.
 *   davonWeiterAbFrist   Davon: danach wurde weitergesprochen.
 *   weiterMaxStillMs     Die größte Stille beim Token unter ALLEN Tokens, nach
 *                        denen es weiterging (`-`: nach keinem).
 *   pausenWeiter         Pausen ab AUTO_ERSTE_FRIST_MS, nach denen der Pegel
 *                        wieder anhaltenden Ton zeigte — mit oder ohne Token.
 *                        Die Pause vor dem ERSTEN Laut zählt nicht.
 *
 *   abstandMs        Beim LETZTEN Token: Zeit vom Ende des letzten bestätigten
 *                    Wortes (Tonachse der Erkennung) bis zur Ankunft des Tokens.
 *   stillMsBeiToken  Beim LETZTEN Token: wie lange der Pegel der Seite da schon
 *                    still war — dieselbe Uhr, an der Jevs Stufen hängen.
 *   weitergesprochen Beim LETZTEN Token: kamen danach noch Wörter? Ja heißt,
 *                    die letzte Pause der Aufnahme hat KEIN `<end>` bekommen.
 *   jev              Jevs Urteil für den Schlusstext (zwei Stellen), sonst `-`.
 *   regel            `stufen` oder `fest` — welche Schwelle Jevs Urteil galt.
 *   entscheid        Wodurch Auto abgeschickt hat: `urteil`, `hoechstens`,
 *                    `rueckfall` (dazu `satz`, `textruhe`, `komma`, `mittel` — unten und in
 *                    shared/stille-auto.ts, `AutoGrund`); sonst `-` (Knopf, feste
 *                    Stille, Wort …). `komma` (07.10.2026): die Obergrenze bei Komma
 *                    am Ende schickte ab, bevor es die von vorher getan hätte. `mittel`
 *                  (08.10.2026): ebenso bei Jev 0,5 bis unter 0,8 (AUTO_MITTEL_HOECHSTENS_MS).
 *                    `zusage` (15.10.2026): der ganze Text war eine kurze Zusage („Okay, und
 *                    weiter“, `kurzeZusage`) — hinaus nach 1,2 s, gleich, was Jev sagte.
 *                    `obergrenze` (16.10.2026): die kurze Obergrenze (4 s, nach Füllwort/Komma 6 s,
 *                    `obergrenzeKurzMs`) schickte ab, bevor es jede Grenze von vorher getan hätte.
 *   stufe, stillMs   Stufe (1…3, 4 = Obergrenze) und Stille in dem Moment.
 *   textRuhtMs       Wie lange der Text beim Abschicken schon unverändert stand
 *                    (nur beim Abschicken durch Auto, sonst `-`).
 *   satzzeichen      (02.10.2026) Die ART des Satzendes im Schlusstext: `?`, `.`,
 *                    `!` oder `keins` (shared/stille-auto.ts, `satzEnde`); `-`
 *                    ohne Text. Nie ein Wort.
 *   satzregel        Galt die Satzzeichen-Regel für Jevs Urteil: `an`/`aus`,
 *                    `-` ohne Urteil. `entscheid=satz` heißt: das Zeichen
 *                    schickte früher ab, als die Regel ohne es getan hätte.
 *
 *   (05.10.2026, „Laut ohne neues Wort“, shared/stille-auto.ts `autoJevStille`)
 *   textruhe         Galt die Regel für Jevs Urteil: `an`/`aus`, `-` ohne Urteil.
 *                    `entscheid=textruhe` heißt: erst die Ruhe des Textes über
 *                    einen Laut ohne Wort hinweg erreichte Jevs Schwelle.
 *   jevStillMs       Nur dann: die Stille, an der Jevs Schwelle gemessen wurde
 *                    (`stillMs` bleibt die des Pegels); sonst `-`.
 *   regelv           AUTO_REGEL_VERSION — welche Fassung der Regel die App rechnet.
 *   ohneWortMaxMs    Die längste Strecke anhaltenden Tons ohne neues Wort, nach
 *                    der DOCH noch Text kam (`-`: keine) — so lange kann Sprechen
 *                    ohne Mitschrift bleiben. Erst ab dem ersten Text gezählt.
 *   ohneWortEndeMs   Ton ohne neues Wort seit der letzten Änderung des Textes bis
 *                    zum Ende der Aufnahme (Geräusch nach dem letzten Wort).
 *   Beide nur gemessen: ob die Karte auch MITTEN in einem Geräusch abschicken
 *   darf, entscheidet erst ihre Verteilung (ohneWortMaxMs muss klar unter der
 *   Schwelle bleiben, ab der sie es täte).
 *
 *   (07.10.2026, die Sprachgrenze, mic-visualizer.ts `SPRACH_ABSTAND`)
 *   altMs            Nur beim Abschicken durch Auto: die Stille in dem Moment nach
 *                    der ALTEN Grenze (Grundpegel, ohne Sprachgrenze) — `stillMs`
 *                    ist die, die entschied. Liegt `altMs` unter der Stufe, hat
 *                    erst die Sprachgrenze abgeschickt (Rückdrehkriterium dort).
 *                    Sonst `-`. ⚠️ Kurz `altMs`, nicht `stillAltMs`: mit fünf
 *                    Ziffern in jedem Feld läge die Zeile sonst bei 404 Zeichen.
 *
 *   (12.10.2026, Messfühler der Bahn „stille-feinschliff“ — nur beim Abschicken durch Auto, sonst `-`)
 *   frageMs          Bei welcher Stille die Frage an Jev gestellt wurde, deren Urteil galt (nur mit Urteil
 *                    für den Schlusstext). Vorher 1.000, bei geschlossenem Satz seit 12.10. 800.
 *   ueberhangMs      Die Stille beim Abschicken (bei `entscheid=textruhe` die, an der Jevs Schwelle gemessen
 *                    wurde) MINUS `fruehMs` des Urteils (`[stille-auto] fruehMs=`: ab wann es nach der Regel
 *                    hätte senden dürfen), nie negativ. Das ist die Zeit, die Antwort, Takt und Auswertung
 *                    über die Regel hinaus brauchten. `-`: Obergrenze/Rückfall oder kein Urteil.
 *   Die Entscheidung `geschlossen` heißt: die Obergrenze 5,0 s für einen geschlossenen Satz bei Jev < 0,5
 *   (AUTO_GESCHLOSSEN_HOECHSTENS_MS) schickte vor der von vorher (6,0 s) ab. Sie kommt NUR vor, wenn der Server
 *   `deckelKurz` meldet (STILLE_DECKEL_KURZ, Standard AUS — vorerst nicht scharf). Ob er später sinnvoll wäre,
 *   zeigen die Fälle mit `entscheid=hoechstens`, `jev=` unter 0.50 und `satzzeichen=` . ? ! (hier 1 s gespart worden
 *   wäre) gegen `[pause-weiter]` und `[ptt-ende] senden … autoRestMs=` (Knopf mitten im Fenster 5–6 s).
 *
 * Mehr als 440 Zeichen schneidet der Server ab (messzeile-route.ts; bis 12.10.2026 400, die neuen Felder brauchten Platz): auch mit
 * fünfstelligen Zahlen in jedem Feld bleibt die Zeile darunter (Test).
 *
 * Auswerten: `[stille-auto]` (Server, je Frage) gegen `[ende-token]` (je
 * Aufnahme): kam das Token vor dem Entscheid (`stillMsBeiToken` < `stillMs`)?
 * Fielen die stillen Fälle (`weitergesprochen=nein`) mit Token zusammen, und
 * kam bei den Denkpausen, nach denen es weiterging, KEIN Token ab der Frist
 * (`davonWeiterAbFrist` klein gegen `pausenWeiter`)? Dann taugt `<end>` als
 * Vorschlag. Die Atempausen (`davonWeiter` ohne `davonWeiterAbFrist`) sagen
 * dazu nichts — Jev fragt dort nie.
 *
 * ⚠️ Bewusst NICHT geändert: die Soniox-Parameter (`endpoint_sensitivity`,
 *    `max_endpoint_delay_ms`, `endpoint_latency_adjustment_level`). Was
 *    gemessen wird, ist das Verhalten mit den heutigen Vorgaben.
 */

import { AUTO_ERSTE_FRIST_MS, AUTO_REGEL_VERSION, type AutoGrund, type SatzEnde } from "../../shared/stille-auto.js";

/** Ist dieses Token das Satzende-Signal der Erkennung? (Genau `<end>`, kein anderes Steuerzeichen.) */
export function istEndeToken(text: string | undefined): boolean {
  return typeof text === "string" && /^<end>$/i.test(text.trim());
}

/**
 * Was die Erkennung beim Ankommen eines `<end>` weiß. `abstandMs` ist `null`,
 * wenn es noch kein bestätigtes Wort oder keine Tonachse gab; `wortzahl` ist
 * die Zahl der bestätigten Wörter DAVOR.
 */
export interface EndeSignal {
  abstandMs: number | null;
  wortzahl: number;
}

interface LetztesToken {
  abstandMs: number | null;
  stillMsBeiToken: number;
  wortzahl: number;
  weiter: boolean;
}

interface EndeMessung {
  /** Lieferte die Live-Mitschrift Text? */
  live: boolean;
  anzahl: number;
  davonWeiter: number;
  /** Tokens, die bei mindestens AUTO_ERSTE_FRIST_MS Stille ankamen (Denkpause statt Atempause). */
  anzahlAbFrist: number;
  /** Davon: danach wurde weitergesprochen. */
  davonWeiterAbFrist: number;
  /** Größte Stille beim Token unter allen Tokens, nach denen es weiterging — `null`: nach keinem. */
  weiterMaxStillMs: number | null;
  /** Pausen ab AUTO_ERSTE_FRIST_MS, nach denen der Pegel wieder anhaltenden Ton zeigte. */
  pausenWeiter: number;
  /** Hat der Pegel schon einmal anhaltenden Ton gezeigt? (Die Pause davor zählt nicht.) */
  tonGehoert: boolean;
  /** Die größte Stille der laufenden Pause. */
  pauseMaxMs: number;
  letztes: LetztesToken | null;
  /** Die letzte bekannte Zahl bestätigter Wörter. */
  wortzahl: number;
  /**
   * Wodurch Auto abschickte — `null`: nicht durch Auto. `jevStillMs` nur bei `grund: "textruhe"`.
   * `stillAltMs` (07.10.2026): die Stille nach der alten Grenze, ohne Sprachgrenze.
   */
  auto: {
    grund: AutoGrund;
    stufe: number;
    stillMs: number;
    textRuhtMs: number | null;
    jevStillMs: number | null;
    stillAltMs: number | null;
    /** 12.10.2026: Stille bei der Frage zum geltenden Urteil. */
    frageMs: number | null;
    /** 12.10.2026: Stille beim Abschicken minus `fruehMs` des Urteils (nie negativ). */
    ueberhangMs: number | null;
  } | null;
  /** 05.10.2026: Kam schon Text? Vorher zählt Ton ohne Wort nicht (das erste Wort braucht seine Zeit). */
  textDa: boolean;
  /** Anhaltender Ton (ms) seit der letzten Änderung des Textes. */
  tonOhneWortMs: number;
  /** Das Größte davon, nach dem doch noch Text kam — `null`: noch keins. */
  ohneWortMaxMs: number | null;
}

export function neueEndeMessung(): EndeMessung {
  return {
    live: false,
    anzahl: 0,
    davonWeiter: 0,
    anzahlAbFrist: 0,
    davonWeiterAbFrist: 0,
    weiterMaxStillMs: null,
    pausenWeiter: 0,
    tonGehoert: false,
    pauseMaxMs: 0,
    letztes: null,
    wortzahl: 0,
    auto: null,
    textDa: false,
    tonOhneWortMs: 0,
    ohneWortMaxMs: null,
  };
}

/**
 * So lange zählt ein Pegelbild höchstens als Ton (ms): Bilder kommen alle
 * 16–100 ms (Browser/nativ); ein längerer Abstand ist eine Lücke im Rechentakt,
 * kein gemessener Ton.
 */
export const TON_BILD_HOECHSTENS_MS = 250;

/**
 * Ein Pegelbild (05.10.2026): `ton` — liegt es in anhaltendem Ton (derselbe, der
 * die Stille beendet); `bildMs` — wie lange seit dem vorigen Bild. Zählt Ton
 * ohne neues Wort, erst ab dem ersten Text.
 */
export function merkeTonOhneWort(m: EndeMessung, ton: boolean, bildMs: number): void {
  if (!ton || !m.textDa || !Number.isFinite(bildMs) || bildMs <= 0) return;
  m.tonOhneWortMs += Math.min(bildMs, TON_BILD_HOECHSTENS_MS);
}

/** Der Text der Mitschrift hat sich geändert: der Ton davor bekam sein Wort. */
export function merkeTextNeu(m: EndeMessung): void {
  if (m.textDa && m.tonOhneWortMs > 0) m.ohneWortMaxMs = Math.max(m.ohneWortMaxMs ?? 0, Math.round(m.tonOhneWortMs));
  m.textDa = true;
  m.tonOhneWortMs = 0;
}

/** Die Live-Mitschrift hat Text geliefert. */
export function merkeLive(m: EndeMessung): void {
  m.live = true;
}

/** Nach diesem Token ging es weiter: einmal zählen, in beiden Zählern (alle / ab der Frist). */
function zaehleWeiter(m: EndeMessung, t: LetztesToken): void {
  t.weiter = true;
  m.davonWeiter++;
  if (t.stillMsBeiToken >= AUTO_ERSTE_FRIST_MS) m.davonWeiterAbFrist++;
  m.weiterMaxStillMs = Math.max(m.weiterMaxStillMs ?? 0, t.stillMsBeiToken);
}

/**
 * Die Zahl bestätigter Wörter ist jetzt `n` (jede Mitschrift-Meldung). Wuchs
 * sie seit dem letzten `<end>`, ging es nach ihm weiter.
 */
export function merkeWortzahl(m: EndeMessung, n: number): void {
  m.wortzahl = n;
  const t = m.letztes;
  if (t && !t.weiter && n > t.wortzahl) zaehleWeiter(m, t);
}

/** Ein `<end>` kam an; `stillMs` ist die Stille der Seite in diesem Moment. */
export function merkeEnde(m: EndeMessung, e: EndeSignal, stillMs: number): void {
  m.anzahl++;
  const still = Math.round(Math.max(0, stillMs));
  if (still >= AUTO_ERSTE_FRIST_MS) m.anzahlAbFrist++;
  const t: LetztesToken = {
    abstandMs: e.abstandMs === null ? null : Math.round(e.abstandMs),
    stillMsBeiToken: still,
    wortzahl: e.wortzahl,
    weiter: false,
  };
  m.letztes = t;
  if (m.wortzahl > e.wortzahl) zaehleWeiter(m, t);
}

/**
 * Der Pegel hat ein Bild geliefert: `stillMs` die laufende Stille, `ton` ob
 * anhaltender Ton belegt ist (mic-visualizer.ts, `tonSeit`). Dauerte die
 * Pause mindestens AUTO_ERSTE_FRIST_MS und kommt jetzt Ton, wurde nach einer
 * Pause weitergesprochen — gleich, ob ein `<end>` dazwischen lag. Die Stille
 * vor dem ersten Laut ist keine Pause in einer Rede und zählt nicht.
 *
 * Rückgabe (02.10.2026, Messpunkt `[pause-weiter]`): die Länge der Pause in ms,
 * wenn sie mindestens PAUSE_WEITER_MESS_MS (1 s) dauerte und jetzt wieder Ton
 * kommt — sonst `null`. Genau einmal je Pause (das Maximum wird danach null).
 */
export function merkePegel(m: EndeMessung, stillMs: number, ton: boolean): number | null {
  if (!ton) {
    if (stillMs > m.pauseMaxMs) m.pauseMaxMs = stillMs;
    return null;
  }
  const pause = m.tonGehoert ? m.pauseMaxMs : 0;
  if (pause >= AUTO_ERSTE_FRIST_MS) m.pausenWeiter++;
  m.tonGehoert = true;
  m.pauseMaxMs = 0;
  return pause >= PAUSE_WEITER_MESS_MS ? Math.round(pause) : null;
}

/** Ab dieser Pause (ms) meldet `[pause-weiter]` die Länge, wenn danach wieder Ton kommt. */
const PAUSE_WEITER_MESS_MS = 1000;

/**
 * Die Journalzeile `[pause-weiter]` — nur Zahlen, kein Inhalt (Jarvis-Tempo, 02.10.2026).
 *
 * `textRuhtMs` (05.10.2026): wie lange der Text stand, als wieder Ton kam. Länger
 * als die Pause heißt: vor der Pause lag ein Laut ohne Wort — nur dort kann „Laut
 * ohne neues Wort“ (shared/stille-auto.ts, `autoJevStille`) eine Fortsetzung
 * abschneiden. So wird das Risiko an echten Fortsetzungen zählbar.
 */
export function pauseWeiterZeile(pauseMs: number, textRuhtMs?: number): string {
  const ruht = textRuhtMs === undefined || !Number.isFinite(textRuhtMs) ? "" : ` textRuhtMs=${Math.round(Math.max(0, textRuhtMs))}`;
  return `stillMs=${Math.round(pauseMs)}${ruht}`;
}

/** Eine Zahl in ms für die Zeile: gerundet, nie negativ — `null` ohne brauchbaren Wert. */
const msWert = (v: number | undefined): number | null =>
  v === undefined || !Number.isFinite(v) ? null : Math.round(Math.max(0, v));

/**
 * Auto hat abgeschickt: Grund, Stufe und Stille dabei, dazu wie lange der Text
 * schon unverändert stand — und bei `textruhe` die Stille, an der Jevs Schwelle
 * gemessen wurde (05.10.2026); dazu die Stille nach der alten Grenze, ohne
 * Sprachgrenze (07.10.2026, mic-visualizer.ts `stilleAltMs`).
 */
export function merkeAutoSenden(
  m: EndeMessung,
  grund: AutoGrund,
  stufe: number,
  stillMs: number,
  textRuhtMs?: number,
  jevStillMs?: number,
  stillAltMs?: number,
  urteil?: { fruehMs: number | null; frageMs: number | null },
): void {
  const still = Math.round(Math.max(0, stillMs));
  const jevStill = msWert(jevStillMs);
  const frueh = urteil?.fruehMs;
  m.auto = {
    grund,
    stufe,
    stillMs: still,
    textRuhtMs: msWert(textRuhtMs),
    jevStillMs: jevStill,
    stillAltMs: msWert(stillAltMs),
    frageMs: msWert(urteil?.frageMs ?? undefined),
    // 12.10.2026: wie weit das Abschicken über die frühest mögliche Stille des Urteils hinausging.
    ueberhangMs: typeof frueh === "number" && Number.isFinite(frueh) ? Math.round(Math.max(0, (jevStill ?? still) - frueh)) : null,
  };
}

/** Jevs Urteil für den Schlusstext — gebraucht, wenn es für genau diesen Text gilt. */
interface EndeUrteil {
  fertig: number | null;
  stufen: boolean;
  /** Galt die Satzzeichen-Regel (02.10.2026)? Fehlt: aus. */
  satzzeichen?: boolean;
  /** Galt „Laut ohne neues Wort“ (05.10.2026)? Fehlt: aus. */
  textruhe?: boolean;
}

const ms = (v: number | null | undefined): string => (v === null || v === undefined ? "-" : String(v));
const anAus = (urteil: EndeUrteil | null, an: boolean | undefined): string => (urteil ? (an === true ? "an" : "aus") : "-");

/** Die Zeile — nur Zahlen und feste Wörter. `satz`: die Art des Satzendes im Schlusstext (`null`: kein Text). */
export function endeZeile(m: EndeMessung, urteil: EndeUrteil | null, satz: SatzEnde | null = null): string {
  const t = m.letztes;
  const fertig = urteil?.fertig ?? null;
  const jev = fertig !== null && Number.isFinite(fertig) ? fertig.toFixed(2) : "-";
  return (
    `live=${m.live ? "ja" : "nein"} vorhanden=${m.anzahl > 0 ? "ja" : "nein"} anzahl=${m.anzahl}` +
    ` davonWeiter=${m.davonWeiter} anzahlAbFrist=${m.anzahlAbFrist} davonWeiterAbFrist=${m.davonWeiterAbFrist}` +
    ` weiterMaxStillMs=${ms(m.weiterMaxStillMs)} pausenWeiter=${m.pausenWeiter}` +
    ` abstandMs=${ms(t?.abstandMs)} stillMsBeiToken=${ms(t?.stillMsBeiToken)}` +
    ` weitergesprochen=${t ? (t.weiter ? "ja" : "nein") : "-"} jev=${jev}` +
    ` regel=${urteil ? (urteil.stufen ? "stufen" : "fest") : "-"}` +
    ` entscheid=${m.auto?.grund ?? "-"} stufe=${m.auto?.stufe ?? "-"} stillMs=${ms(m.auto?.stillMs)}` +
    ` textRuhtMs=${ms(m.auto?.textRuhtMs)}` +
    ` satzzeichen=${satz ?? "-"} satzregel=${anAus(urteil, urteil?.satzzeichen)}` +
    ` textruhe=${anAus(urteil, urteil?.textruhe)} jevStillMs=${ms(m.auto?.jevStillMs)} regelv=${AUTO_REGEL_VERSION}` +
    ` ohneWortMaxMs=${ms(m.ohneWortMaxMs)} ohneWortEndeMs=${Math.round(m.tonOhneWortMs)}` +
    ` altMs=${ms(m.auto?.stillAltMs)}` +
    // Zwei Zeilen, damit der Umlaut-Wächter (tests/frontend/umlaute.test.ts) den ASCII-Schlüssel nicht als Schirmtext liest.
    ` frageMs=${ms(m.auto?.frageMs)}` +
    ` ueberhangMs=${ms(m.auto?.ueberhangMs)}`
  );
}
